/**
 * "Share with": emails typed comma- or space-separated, each shown as a
 * removable chip. People who already signed in to the workbench are suggested
 * by name and email as you type (arrow keys and Enter pick one; Enter on
 * typed text adds it). Owners and admins may add anyone at qure.ai; a member
 * only someone who has signed in — the server decides, this only explains it.
 */

import { useId, useRef, useState } from "react";
import { X } from "lucide-react";
import { LabelMark } from "@/components/ui/field";
import { SuggestionList, useSuggestions, type Suggestion } from "@/components/ui/suggestions";
import { useInvitees, useProject } from "@/features/projects/hooks";
import type { Invitee } from "@/features/projects/types";
import { splitEmails } from "./scores";

/** Who matches what is being typed (the text after the last comma), by name or email. */
function matching(invitees: Invitee[], text: string, taken: string[]): Suggestion[] {
  const q =
    text
      .split(/[\s,;]+/)
      .pop()
      ?.trim()
      .toLowerCase() ?? "";
  return invitees
    .filter(
      (p) => !taken.includes(p.email) && (!q || p.email.includes(q) || p.name.toLowerCase().includes(q)),
    )
    .map((p) => ({ value: p.email, label: p.name, hint: p.email }));
}

export function EmailsInput({
  value,
  onChange,
  label = "Share with",
}: {
  value: string[];
  onChange: (emails: string[]) => void;
  label?: string;
}) {
  const id = useId();
  const box = useRef<HTMLInputElement>(null);
  const { can } = useProject();
  const invitees = useInvitees(can("annotations:write"));
  const [text, setText] = useState("");
  const shown = matching(invitees.data ?? [], text, value);

  const add = (raw: string) => {
    const fresh = splitEmails(raw).filter((e) => !value.includes(e));
    if (fresh.length) onChange([...value, ...fresh]);
    setText("");
  };
  /** The typed text up to the last separator, plus the picked email. Closes
   *  the list so the new chip shows; typing or ↓ opens it again. */
  const pick = (s: Suggestion) => (add(`${text.replace(/[^\s,;]*$/, "")} ${s.value}`), list.close());
  const list = useSuggestions(shown, pick);

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-semibold">
        {label}
        <LabelMark optional />
      </label>
      {value.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0" aria-label="Reviewers">
          {value.map((email) => (
            <li
              key={email}
              className="flex items-center gap-1 rounded-full bg-chip py-0.5 pr-1 pl-2.5 text-xs text-chip-ink"
            >
              {email}
              <button
                type="button"
                className="grid size-4 place-items-center rounded-full hover:bg-hover"
                aria-label={`Remove ${email}`}
                onClick={() => onChange(value.filter((e) => e !== email))}
              >
                <X size={11} strokeWidth={2.2} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        ref={box}
        id={id}
        {...list.inputProps}
        className="inp"
        placeholder="name@qure.ai, another@qure.ai"
        value={text}
        onFocus={() => list.show(0)}
        onClick={() => !list.open && list.show(0)}
        onChange={(e) => {
          const typed = e.target.value;
          if (/[\s,;]$/.test(typed)) add(typed);
          else setText(typed);
          list.show(0);
        }}
        onKeyDown={(e) => {
          if (!list.onKeyDown(e) && e.key === "Enter" && text.trim()) {
            e.preventDefault();
            add(text);
          }
        }}
        onBlur={() => {
          list.close();
          if (text.trim()) add(text);
        }}
      />
      <SuggestionList anchor={box} state={list} options={shown} label={label} onPick={pick} />
      <span className="text-xs leading-snug text-muted">
        These people can review this queue. Anyone not in the project joins as an Annotator.
        {!can("members:write") &&
          " You can add people who have signed in to the workbench. Ask a project owner or admin to add someone new."}
      </span>
    </div>
  );
}
